import { Controller } from 'stimulus'

// Results Summary section on a task's Results tab. The page carries a
// placeholder (data-task-results-summary-lazy) and this fetches the section
// body once connected, so building the summary doesn't hold up the page. It
// then keeps the summary in step with the grid above it by fetching the body
// again, debounced, whenever results change: a remote form or link inside
// the tab succeeds (cell saves, row actions), a response says so outright
// (task-results:changed), or a broadcast reloads the grid
// (resourceable:reloaded). Pinned samples survive the swap. Only the latest
// request's answer is applied, so a slow first load can't overwrite a newer
// refetch; if the first load fails, the placeholder says so and offers a retry.
const SETTLE_MS = 1200
const FAILED_HTML = '<div class="text-sm text-gray-500 py-2">Couldn\'t load the summary. Reload the page to try again, or '
  + '<button type="button" class="link rs-section__retry" data-action="click->task-results-summary#retry">try again now</button>.</div>'

export default class extends Controller {
  static targets = ['body'];

  connect() {
    this.onAjax = this.onAjax.bind(this)
    this.schedule = this.schedule.bind(this)
    document.addEventListener('ajax:success', this.onAjax)
    document.addEventListener('task-results:changed', this.schedule)
    document.addEventListener('resourceable:reloaded', this.schedule)
    this.requests = 0
    // Without lazy the page already carries the body (nothing to summarize).
    this.shown = this.data.get('lazy') !== 'true'
    if (!this.shown) { this.load() }
  }

  disconnect() {
    document.removeEventListener('ajax:success', this.onAjax)
    document.removeEventListener('task-results:changed', this.schedule)
    document.removeEventListener('resourceable:reloaded', this.schedule)
    clearTimeout(this.timer)
  }

  onAjax(event) {
    const tab = this.element.closest('.task-sections')
    if (!tab || !tab.contains(event.target) || this.element.contains(event.target)) { return }
    this.schedule()
  }

  schedule() {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.load(), SETTLE_MS)
  }

  retry(event) {
    event.preventDefault()
    this.load()
  }

  load() {
    this.requests += 1
    const request = this.requests
    const latest = () => request === this.requests && this.hasBodyTarget
    fetch(this.data.get('url'), {
      credentials: 'same-origin',
      headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(response => (response.ok ? response.text() : Promise.reject(response)))
      .then((html) => {
        if (!latest()) { return }
        this.bodyTarget.innerHTML = html
        this.shown = true
        const summary = this.application.getControllerForElementAndIdentifier(this.element, 'results-summary')
        if (summary) {
          summary.applyPins()
          summary.applyTrace(null)
          summary.renderTray()
        }
      })
      .catch(() => {
        // A failed refetch keeps the summary already shown.
        if (latest() && !this.shown) { this.bodyTarget.innerHTML = FAILED_HTML }
      })
  }
}
